import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, PlayCircle, RotateCcw, Save, TimerOff } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { api, errMsg, fmtDateTime } from "../../lib/api";

/**
 * Phase 210 — SAF auto-escalation time limits (hours). Shared by Admin → Settings and Super Admin → Tenant Config.
 *  - Collector limit: a case idle with the Field Collector for this long escalates to the Supervisor (or to the Admin when no
 *    supervisor covers the ward). The clock restarts every time the case lands with a collector (assign / reassign / send back).
 *  - Supervisor limit: a verified case idle with the Supervisor escalates to the Municipal Admin.
 * A platform cron runs the check every 15 minutes; "Run check now" runs it for this municipality immediately.
 */
const STAGES = [
  { key: "collector", label: "Field Collector limit", to: "Supervisor", hint: "Idle with the Field Collector (pending verification or sent back) → escalates to the Supervisor; to the Municipal Admin when no supervisor covers the ward." },
  { key: "supervisor", label: "Supervisor limit", to: "Municipal Admin", hint: "Verified case idle with the Supervisor → escalates to the Municipal Admin." },
];

const hoursText = (h) => {
  const n = Number(h) || 0;
  if (n % 24 === 0 && n >= 24) { const d = n / 24; return `${n} h (${d} day${d === 1 ? "" : "s"})`; }
  return `${n} h`;
};

export function SafEscalationSettingsCard({ code, readOnly = false, testIdPrefix = "saf-escalation", onSwept, className = "" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ collector_hours: "", supervisor_hours: "" });
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(() => {
    setError(null);
    return api.get(`/t/${code}/settings/saf-escalation`).then((r) => {
      setData(r.data);
      setForm({ collector_hours: String(r.data.collector_hours ?? 24), supervisor_hours: String(r.data.supervisor_hours ?? 24) });
    }).catch((e) => setError(errMsg(e)));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const min = data?.min_hours ?? 1, max = data?.max_hours ?? 720;
  const parse = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : NaN; };
  const invalid = (k) => { const n = parse(form[k]); return Number.isNaN(n) || n < min || n > max; };
  const anyInvalid = STAGES.some((s) => invalid(`${s.key}_hours`));
  const dirty = data && (parse(form.collector_hours) !== data.collector_hours || parse(form.supervisor_hours) !== data.supervisor_hours);
  const isDefault = data && STAGES.every((s) => parse(form[`${s.key}_hours`]) === (data.defaults?.[`${s.key}_hours`] ?? 24));

  const save = async () => {
    if (anyInvalid) return toast.error(`Each limit must be a whole number between ${min} and ${max} hours`);
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/settings/saf-escalation`, { collector_hours: parse(form.collector_hours), supervisor_hours: parse(form.supervisor_hours) });
      setData((d) => ({ ...(d || {}), ...r.data }));
      setForm({ collector_hours: String(r.data.collector_hours), supervisor_hours: String(r.data.supervisor_hours) });
      toast.success("Auto-escalation limits saved", { description: `Field Collector ${hoursText(r.data.collector_hours)} · Supervisor ${hoursText(r.data.supervisor_hours)}. Applies to cases that enter a stage from now on.` });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const resetDefaults = () => {
    setForm({ collector_hours: String(data?.defaults?.collector_hours ?? 24), supervisor_hours: String(data?.defaults?.supervisor_hours ?? 24) });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/t/${code}/saf/escalations/run`);
      setLastRun(r.data);
      const n = r.data.escalated_count || 0;
      if (n > 0) toast.success(`${n} self-assessment${n === 1 ? "" : "s"} escalated`, { description: r.data.escalated.map((x) => `${x.saf_id}: ${x.from} → ${x.to}`).join(" · ") });
      else toast.info("Escalation check complete — nothing was overdue", { description: `${r.data.checked} open case${r.data.checked === 1 ? "" : "s"} checked${r.data.backfilled ? `, ${r.data.backfilled} given a deadline` : ""}.` });
      onSwept?.(r.data);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };

  const oc = data?.open_counts || {};

  return (
    <Card className={`shadow-sm ${className}`} data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2 flex-wrap">
          <TimerOff className="h-4 w-4" /> SAF auto-escalation time limits
          {data && (
            <Badge variant="outline" className={`${isDefault ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-rose-50 text-rose-700 border-rose-200"} tabular-nums`} data-testid={`${testIdPrefix}-summary`}>
              Collector {hoursText(data.collector_hours)} · Supervisor {hoursText(data.supervisor_hours)}
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          A citizen self-assessment that nobody acts on within the limit moves to the next role automatically and everyone involved is notified.
          If the Field Collector misses the deadline the case goes to the Supervisor; if the Supervisor misses it the case goes to the Municipal Admin.
          The Field Collector clock restarts whenever a case is (re)assigned or sent back. The check runs every 15 minutes. Default {hoursText(data?.defaults?.collector_hours ?? 24)}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <div className="text-xs text-destructive flex items-center justify-between gap-2" data-testid={`${testIdPrefix}-error`}>
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={load} data-testid={`${testIdPrefix}-retry`}>Try again</Button>
          </div>
        ) : !data ? (
          <div className="space-y-2" data-testid={`${testIdPrefix}-loading`}><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
        ) : (
          <>
            <fieldset disabled={readOnly} className="grid gap-3 sm:grid-cols-2 min-w-0">
              {STAGES.map((s) => {
                const k = `${s.key}_hours`;
                const bad = invalid(k) && form[k] !== "";
                return (
                  <div key={s.key} className="space-y-1" data-testid={`${testIdPrefix}-${s.key}-field`}>
                    <Label htmlFor={`${testIdPrefix}-${s.key}-input`} className="text-xs">{s.label} (hours)</Label>
                    <div className="flex items-center gap-2">
                      <Input id={`${testIdPrefix}-${s.key}-input`} type="number" inputMode="numeric" min={min} max={max} step={1} value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`w-28 tabular-nums ${bad ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                        aria-invalid={bad ? "true" : undefined} data-testid={`${testIdPrefix}-${s.key}-input`} />
                      <span className="text-xs text-muted-foreground">→ {s.to}</span>
                    </div>
                    <p className={`text-[11px] ${bad ? "text-red-700 font-medium" : "text-muted-foreground"}`} data-testid={`${testIdPrefix}-${s.key}-hint`}>
                      {bad ? `Whole number between ${min} and ${max} hours.` : s.hint}
                    </p>
                    {data[`${s.key}_updated_at`] && (
                      <p className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-${s.key}-updated`}>
                        Last changed {fmtDateTime(data[`${s.key}_updated_at`])}{data[`${s.key}_updated_by_name`] ? ` by ${data[`${s.key}_updated_by_name`]}` : ""}
                      </p>
                    )}
                  </div>
                );
              })}
            </fieldset>

            <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`${testIdPrefix}-open-counts`}>
              <span className="text-muted-foreground">Open now:</span>
              <Badge variant="outline" className="tabular-nums bg-sky-50 text-sky-700 border-sky-200" data-testid={`${testIdPrefix}-count-collector`}>{oc.collector ?? 0} with Collector</Badge>
              <Badge variant="outline" className="tabular-nums bg-amber-50 text-amber-700 border-amber-200" data-testid={`${testIdPrefix}-count-supervisor`}>{oc.supervisor ?? 0} with Supervisor</Badge>
              <Badge variant="outline" className="tabular-nums bg-violet-50 text-violet-700 border-violet-200" data-testid={`${testIdPrefix}-count-admin`}>{oc.admin ?? 0} with Admin</Badge>
              <Badge variant="outline" className="tabular-nums bg-rose-50 text-rose-700 border-rose-200" data-testid={`${testIdPrefix}-count-escalated`}>{oc.escalated_open ?? 0} auto-escalated &amp; open</Badge>
            </div>

            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={save} disabled={busy || anyInvalid || !dirty} data-testid={`${testIdPrefix}-save`}>
                  <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save limits"}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={resetDefaults} disabled={busy || isDefault} data-testid={`${testIdPrefix}-reset`}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to {hoursText(data.defaults?.collector_hours ?? 24)}
                </Button>
                <Button type="button" variant="outline" size="sm" className="sm:ml-auto" onClick={runNow} disabled={running} data-testid={`${testIdPrefix}-run-now`}>
                  <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> {running ? "Checking…" : "Run escalation check now"}
                </Button>
              </div>
            )}
            {dirty && !anyInvalid && !readOnly && (
              <p className="text-[11px] text-amber-800 flex items-center gap-1.5" data-testid={`${testIdPrefix}-dirty-note`}>
                <AlertTriangle className="h-3.5 w-3.5" /> Unsaved changes. New limits apply to cases that enter a stage after saving; cases already waiting keep their current deadline.
              </p>
            )}
            {lastRun && (
              <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-1" data-testid={`${testIdPrefix}-last-run`}>
                <p className="font-medium">Last check {fmtDateTime(lastRun.at)} — {lastRun.checked} open case{lastRun.checked === 1 ? "" : "s"} checked, {lastRun.escalated_count} escalated{lastRun.backfilled ? `, ${lastRun.backfilled} given a deadline` : ""}{lastRun.errors ? `, ${lastRun.errors} error(s)` : ""}.</p>
                {lastRun.escalated?.length > 0 && (
                  <ul className="list-disc pl-4 space-y-0.5">
                    {lastRun.escalated.map((x) => <li key={x.saf_id} data-testid={`${testIdPrefix}-last-run-${x.saf_id}`}><span className="font-mono">{x.saf_id}</span>: {x.from} → {x.to}</li>)}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
